import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { useParams } from 'react-router';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import { Button, Checkbox, TextField, Autocomplete } from '@mui/material';
import { FILE_STATUS } from '@/helpers/constants';
import useError from '@/hooks/useError';
import useSave from '@/hooks/useSave';
import useToastContext from '@/hooks/useToastContext';
import { copyPlantAttachments } from '@/redux/Campaign/campaign.action';
import { getCampaignVehicles, getCampaignVehiclesVolumes, getVolumeHeaderPlants, getSelectedPlants, getOriginalValues } from '@/redux/Campaign/campaign.reducer';
import { getSettingsInfo } from '@/redux/User/user.reducer';
import campaignServices from '@/services/campaignServices';
import { AffectedVehicles, AffectedVehiclesOriginalValues, Plant, RouterParams, VehicleFile, Volume } from '@/types';
import { mapAggregateModelSelections } from '../VolumeDetails/volumeDetails-utils';
import { isViaPlants } from '../../SeriesPanel/SeriesRow/series-row-utils';
import styles from '../../AffectedVehicles.module.scss';

interface VolumeCopyProps {
  headerPlants: Plant[];
  plantSelected: Plant;
  setShowCopyData: (active: boolean) => void;
  copyAttachments: (volumes: Volume[]) => void;
  timeZone: string;
  volumes: Volume[];
  originalValues: AffectedVehiclesOriginalValues;
  plants: Plant[];
  affectedVehicles: AffectedVehicles;
}

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

const VolumeCopy: React.FC<VolumeCopyProps> = (props) => {
  const { headerPlants, plantSelected, setShowCopyData, copyAttachments, timeZone, volumes, originalValues, plants,
    affectedVehicles } = props;

  const { id, version } = useParams<RouterParams>();
  const { t } = useTranslation();
  const { handleCriticalError } = useError();
  const { addToast } = useToastContext();
  const saveChanges = useSave('affected-vehicles');

  const getSelectedVolume = () => {
    const selectedVolume = originalValues.values?.volumes?.find((vol: Volume) => vol.plant === plantSelected?.wkzNumber);

    return selectedVolume === undefined
      ? volumes?.find((vol) => vol.plant === plantSelected?.wkzNumber)
      : selectedVolume;
  };

  const plantsSelectedToCopy = () => {
    const plantList = isViaPlants(affectedVehicles) ? plants : headerPlants;

    return plantList.filter((result: Plant) => result.wkzNumber !== plantSelected?.wkzNumber)
      .map((p) => ({ plantName: p.plantName }));
  };

  const [plantsCopy, setPlantsCopy] = useState<Plant[]>([]);
  const [filesCopy, setFilesCopy] = useState<VehicleFile[]>(getSelectedVolume()?.vehicleRestrictionFiles);

  useEffect(() => {
    if (originalValues.hasChange) saveChanges();
  }, [filesCopy]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setFilesCopy(getSelectedVolume()?.vehicleRestrictionFiles);
  }, [originalValues]); // eslint-disable-line react-hooks/exhaustive-deps

  const handlePlants = (value) => {
    const plantList = isViaPlants(affectedVehicles) ? plants : headerPlants;
    const listPlantsFound = value.map((val) => plantList.find((hp) => hp.plantName === val.plantName));
    setPlantsCopy(listPlantsFound);
  };

  const handleFiles = (value: VehicleFile[]) => {
    setFilesCopy(value);
  };

  const handleCopyPlants = async () => {
    if (originalValues.hasChange) await saveChanges();

    const body = {
      targetPlants: plantsCopy.map(({ wkzNumber }) => wkzNumber),
      filesToCopy: filesCopy.map((file) => file.id) || [],
    };

    const listFormat = (items) =>
      items.map((item) => plants.find((plant) => plant.wkzNumber === item.wkzNumber)?.plantName)
        .filter(Boolean)
        .join(', ');

    campaignServices.copyPlant(id, plantSelected?.wkzNumber, body).then((res) => {
      campaignServices.getCampaignRetry(id, 'limitation-volumes', version).then((limitationVolumes) => {
        copyAttachments(mapAggregateModelSelections(limitationVolumes.volumes, timeZone));
        const incompletedCopied = res.filter((item) => item.partialCopy);
        const completedList = res.filter((item) => !item.partialCopy);

        if (incompletedCopied.length > 0 && completedList.length > 0) {
          addToast(t('affectedVehicles.copyDataIncomplete', { x: listFormat(incompletedCopied), y: listFormat(completedList) }), 'Warning');
        } else if (incompletedCopied.length > 0) {
          addToast(t('affectedVehicles.copyDataOnlyIncomplete', { x: listFormat(incompletedCopied) }), 'Warning');
        } else {
          addToast(t(`affectedVehicles.${body.filesToCopy.length > 0 ? 'copySuccess' : 'copyDataSuccess'}`), 'Success');
        }

        setShowCopyData(false);
      }).catch(handleCriticalError);
    }).catch(handleCriticalError);
  };

  const availableFiles = getSelectedVolume()?.vehicleRestrictionFiles
    ?.filter((file: VehicleFile) => file.status === FILE_STATUS.COMPLETED) ?? [];

  return (
    <div className={styles.CopyData}>
      <span className={styles.TextCopyData}>{t('affectedVehicles.copyPlantData')}</span>
      <span className={styles.EntriesText}>{t('affectedVehicles.existingEntries')}</span>
      <div className={styles.VerticalLine} />
      <span className={styles.CopyEntriesText}>
        {availableFiles.length > 0
          ? t('affectedVehicles.copyFilesFrom')
          : t('affectedVehicles.copyEntriesFrom1')}
      </span>
      <span className={styles.CopyEntriesTextBold}>
        {t('affectedVehicles.copyEntriesFrom2', { x: plantSelected.plantName })}
      </span>
      {availableFiles.length > 0 && (
        <Autocomplete
          multiple
          id="copy-files-selected"
          options={availableFiles}
          disableCloseOnSelect
          getOptionLabel={(option) => option.name}
          value={filesCopy}
          renderOption={(propss, option, { selected }) => (
            <li {...propss}>
              <Checkbox
                color="primary"
                icon={icon}
                checkedIcon={checkedIcon}
                style={{ marginRight: 8 }}
                checked={selected}
              />
              {option.fullName}
            </li>
          )}
          style={{ minWidth: 175, marginLeft: 5, marginRight: 5 }}
          renderInput={(params) => (
            <TextField {...params} />
          )}
          onChange={(event, value) => handleFiles(value)}
        />
      )}
      <span className={styles.CopyEntriesText}>{t('affectedVehicles.copyEntriesFrom3')}</span>
      <Autocomplete
        multiple
        id="copy-plants-selected"
        options={plantsSelectedToCopy()}
        disableCloseOnSelect
        value={plantsCopy}
        getOptionLabel={(option) => option?.plantName}
        isOptionEqualToValue={(option, value) => option?.plantName === value.plantName}
        renderOption={(propss, option, { selected }) => (
          <li {...propss}>
            <Checkbox
              color="primary"
              icon={icon}
              checkedIcon={checkedIcon}
              style={{ marginRight: 8 }}
              checked={selected}
            />
            {option?.plantName}
          </li>
        )}
        style={{ minWidth: 175, marginLeft: 5 }}
        renderInput={(params) => (
          <TextField {...params} />
        )}
        onChange={(event, value) => handlePlants(value)}
      />
      <Button
        id="copy-plants-ok-btn"
        disabled={plantsCopy?.length === 0
          || (availableFiles > 0 && filesCopy.length === 0)}
        onClick={handleCopyPlants}
        color="primary"
      >
        {t('dialog.ok')}
      </Button>
      <Button
        id="copy-plants-cancel-btn"
        variant="contained"
        onClick={() => setShowCopyData(false)}
        color="primary"
      >
        {t('dialog.cancel')}
      </Button>
    </div>
  );
};

const mapStateToProps: any = (state) => ({
  affectedVehicles: getCampaignVehicles(state),
  headerPlants: getVolumeHeaderPlants(state),
  volumes: getCampaignVehiclesVolumes(state),
  plants: getSelectedPlants(state) || [],
  timeZone: getSettingsInfo(state)?.timezone,
  originalValues: getOriginalValues(state),
});

const actionCreators = {
  copyAttachments: copyPlantAttachments,
};

export default connect(mapStateToProps, actionCreators)(VolumeCopy);
